import { animate } from "motion/react"

import { useEffect, useRef, useState } from "react"

const DEFAULT_DURATION_MS = 1500

export const useCountUpOnView = (
  targetValue: number,
  durationMs: number = DEFAULT_DURATION_MS
) => {
  const intersectionElementRef = useRef<HTMLDivElement | null>(null)
  const [displayedValue, setDisplayedValue] = useState(0)
  const hasAnimatedRef = useRef(false)

  const resolvedDurationMs =
    Number.isFinite(durationMs) && durationMs > 0
      ? durationMs
      : DEFAULT_DURATION_MS

  useEffect(() => {
    const intersectionElement = intersectionElementRef.current
    if (hasAnimatedRef.current) {
      setDisplayedValue(targetValue)
      return
    }
    if (!intersectionElement || typeof IntersectionObserver === "undefined") {
      setDisplayedValue(targetValue)
      return
    }

    let controls: ReturnType<typeof animate> | null = null

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || hasAnimatedRef.current) continue
          hasAnimatedRef.current = true

          controls = animate(0, targetValue, {
            duration: resolvedDurationMs / 1000,
            ease: "easeOut",
            onUpdate: (latest) => setDisplayedValue(Math.round(latest)),
          })
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(intersectionElement)
    return () => {
      observer.disconnect()
      controls?.stop()
    }
  }, [targetValue, resolvedDurationMs])

  return { intersectionElementRef, displayedValue }
}
